import React from 'react';

import {PDFDownloadLink} from '@react-pdf/renderer';
import CSSModules from 'react-css-modules';
import {useIntl} from 'react-intl';

import {Button, DownloadIcon, LoadingIndicator, Text} from '@edume/magnificent';

import {useDispatch, useSelector} from '../../../../state/hooks';
import {createSuccessNotification} from '../../../../state/notification/notificationActions';
import {useGetPublicLinkCodeQuery} from '../../../../state/publicLinkCode/publicLinkCodeApi';
import {getLogoUrls} from '../../../../state/settings/settingsSelectors';
import {getSelectedGroupId} from '../../../../state/team/teamSelectors';
import {createPublicUploaderLink} from '../../../../utils/links';
import {QRCodePDFWithDescription} from '../../../QRCodeWithDownload/QRCodePDFWithDescription';
import {useQRCodeDataUrl} from '../../../QRCodeWithDownload/useQRCode';

import styles from './styles.module.scss';

const MediaLibraryPublicLinkComponent = () => {
  const dispatch = useDispatch();
  const groupId = useSelector(getSelectedGroupId);
  const {data: publicLinkCode} = useGetPublicLinkCodeQuery(groupId);
  const logoUrls = useSelector(getLogoUrls);

  const url = publicLinkCode ? createPublicUploaderLink(publicLinkCode) : null;
  const QRCodeDataUrl = useQRCodeDataUrl(url);
  const intl = useIntl();

  if (!QRCodeDataUrl) {
    return (
      <div styleName='qr-code-box'>
        <LoadingIndicator indicator='hexagons' containerStyle='centerFlex' />
      </div>
    );
  }

  const onCopyLink = () => {
    if (url) {
      navigator.clipboard.writeText(url);
      dispatch(createSuccessNotification('Link.copied'));
    }
  };

  return (
    <>
      <div styleName='description-container'>
        <Text size='m'>
          {intl.formatMessage({id: 'VideoLibrary.mediaUpload.description'})}
        </Text>
      </div>
      <div styleName='qr-code-box'>
        <div>
          <Text size='m' weight='bold'>
            {intl.formatMessage({id: 'VideoLibrary.publicLink.download.title'})}
          </Text>

          <Text size='m'>
            {intl.formatMessage({id: 'VideoLibrary.publicLink.download.text'})}
          </Text>
        </div>

        <div styleName='buttons-container'>
          <PDFDownloadLink
            fileName={intl.formatMessage({
              id: 'VideoLibrary.publicLink.qrCode.documentTitle',
            })}
            document={
              <QRCodePDFWithDescription
                QRCodeDataUrl={QRCodeDataUrl!}
                title={intl.formatMessage({
                  id: 'VideoLibrary.publicLink.qrCode.title',
                })}
                description={intl.formatMessage({
                  id: 'VideoLibrary.publicLink.qrCode.description',
                })}
                logo={logoUrls.darkLogoUrl}
              />
            }
          >
            {({loading}) => (
              <Button
                noMarginTop={true}
                type='thirdary'
                icon={<DownloadIcon size='small' colour='brandPrimary' />}
                disabled={loading || !url}
              >
                {intl.formatMessage({
                  id: 'VideoLibrary.publicLink.download.button',
                })}
              </Button>
            )}
          </PDFDownloadLink>

          <div>
            <Button
              type='thirdary'
              onClick={onCopyLink}
              disabled={!url}
              dataAutomation='copy-link-button'
            >
              {intl.formatMessage({id: 'Link.copy'})}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export const MediaLibraryPublicLink = CSSModules(
  MediaLibraryPublicLinkComponent,
  styles
);
